// Dev-delivery notification popups: a scene-independent DOM modal (scrim +
// panel + graphic + OK), shown one at a time. Pure queue split from the DOM
// so it unit-tests; the DOM sink reuses the bundled atlas art as <img> URLs.
import './devPopup.css';

export type DevPopupKind =
  | 'coins'
  | 'bags'
  | 'unlock'
  | 'op'
  | 'skip'
  | 'remove'
  | 'ban'
  | 'potion'
  | 'debris'
  | 'capycoins'
  | 'darkcoins'
  | 'lightcoins'
  | 'capsules'
  | 'goldenbags'
  | 'icon'
  | 'item';

export const POPUP_COPY: Record<
  DevPopupKind,
  { text: string; art: string | null; danger: boolean }
> = {
  coins: { text: 'You have been gifted', art: 'coin', danger: false },
  bags: { text: 'Someone has ordered', art: 'takeout-normal', danger: false },
  unlock: { text: 'A mystery has been unlocked', art: 'mystery-unlock', danger: false },
  op: { text: 'The admin has come', art: 'tower-wise-1', danger: false },
  // /skip (spec 2026-10-04): the wait is over — bags and capsule ready, level won.
  skip: { text: 'Time has been skipped!', art: 'takeout-normal', danger: false },
  // /remove (spec 2026-10-06): an admin took something back.
  remove: {
    text: 'An admin has removed something from your inventory',
    art: 'takeout-normal',
    danger: false,
  },
  ban: { text: 'You have been banned by a dev or admin', art: null, danger: true },
  // The art is replaced per delivery with the gifted potion's own (see enqueueDevPopup).
  potion: { text: 'A potion has been gifted to your Storage', art: 'potion-pickle', danger: false },
  debris: {
    text: 'Space Debris has been added to your Storage',
    art: 'item-debris',
    danger: false,
  },
  capycoins: { text: 'You have been gifted Capycoins', art: 'capycoin', danger: false },
  darkcoins: { text: 'You have been gifted Dark Hambi Coins', art: 'darkcoin', danger: false },
  lightcoins: { text: 'You have been gifted Light Hambi Coins', art: 'lightcoin', danger: false },
  capsules: { text: 'You have been gifted a Capsule', art: 'capsule-capy', danger: false },
  goldenbags: {
    text: 'Someone has ordered you a Golden Takeout Bag',
    art: 'takeout-golden',
    danger: false,
  },
  // Art replaced per delivery with the gifted icon's own (see enqueueDevPopup).
  icon: { text: 'An avatar icon has been gifted to you', art: 'icon-crown', danger: false },
  // Art replaced per delivery with the gifted item's own (see enqueueDevPopup).
  item: { text: 'An item has been added to your Storage', art: 'diamond', danger: false },
};

export class PopupQueue {
  private queue: { kind: DevPopupKind; onOk?: () => void; art?: string }[] = [];
  private current: { kind: DevPopupKind; onOk?: () => void; art?: string } | null = null;

  constructor(private readonly show: (kind: DevPopupKind, art?: string) => void) {}

  push(kind: DevPopupKind, onOk?: () => void, art?: string): void {
    this.queue.push({ kind, onOk, art });
    if (!this.current) this.next();
  }

  /** The visible popup's OK was pressed: fire its callback, show the next. */
  ok(): void {
    const done = this.current;
    this.current = null;
    done?.onOk?.();
    this.next();
  }

  private next(): void {
    const item = this.queue.shift();
    if (!item) return;
    this.current = item;
    this.show(item.kind);
  }
}

// ── DOM singleton ───────────────────────────────────────────────────────────
// Same bundling mechanism as BootScene's atlas loader: the glob resolves to
// hashed asset URLs at build time, so the DOM shows exactly the game's art.
// The opaque scenery BootScene ships as JPEG is left out: no popup shows it,
// and globbing it here would put every background PNG back into the build.
const ART_URLS = import.meta.glob(
  [
    '/assets/atlas/*.png',
    '!/assets/atlas/bg-*.png',
    '!/assets/atlas/splash-hambi.png',
    '!/assets/atlas/splash-nugget.png',
    '!/assets/atlas/thumb-*.png',
    '!/assets/atlas/origin-*.png',
  ],
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;

let domQueue: PopupQueue | null = null;
let scrim: HTMLDivElement | null = null;

function showDom(kind: DevPopupKind, artOverride?: string): void {
  const copy = { ...POPUP_COPY[kind], art: artOverride ?? POPUP_COPY[kind].art };
  scrim = document.createElement('div');
  scrim.className = 'dev-popup-scrim';
  // Phaser listens for pointer events at the window level, so a click on the
  // popup (OK included) would bubble up and ALSO activate whatever game
  // element sits underneath it. Swallow everything at the scrim.
  for (const type of [
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'touchstart',
    'touchend',
  ]) {
    scrim.addEventListener(type, (e) => e.stopPropagation());
  }
  const panel = document.createElement('div');
  panel.className = copy.danger ? 'dev-popup dev-popup-danger' : 'dev-popup';
  panel.dataset.testid = 'dev-popup';
  const url = copy.art ? ART_URLS[`/assets/atlas/${copy.art}.png`] : undefined;
  if (url) {
    const img = document.createElement('img');
    img.className = 'dev-popup-art';
    img.alt = '';
    img.src = url;
    panel.append(img);
  }
  const text = document.createElement('div');
  text.className = 'dev-popup-text';
  text.dataset.testid = 'dev-popup-text';
  text.textContent = copy.text;
  const ok = document.createElement('button');
  ok.className = 'dev-popup-ok';
  ok.dataset.testid = 'dev-popup-ok';
  ok.textContent = 'OK';
  ok.addEventListener('click', () => {
    scrim?.remove();
    scrim = null;
    domQueue?.ok();
  });
  panel.append(text, ok);
  scrim.append(panel);
  document.body.append(scrim);
}

/** Queue a dev popup (creates the overlay lazily on first use). */
export function enqueueDevPopup(
  kind: DevPopupKind,
  opts?: { onOk?: () => void; art?: string },
): void {
  domQueue ??= new PopupQueue(showDom);
  domQueue.push(kind, opts?.onOk, opts?.art);
}
